// modals/store/StoreDetailModal.js
import React, { useRef, useState } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import VerticalFlex from "layouts/flex/VerticalFlex";
import P from "components/P";
import FlexChild from "layouts/flex/FlexChild";
import HorizontalFlex from "layouts/flex/HorizontalFlex";
import { useTranslation } from "react-i18next";
import puffuIcon from "resources/img/puffu/puffuIcon.svg";

const StoreDetailModal = NiceModal.create(({ store, isMapView = false }) => {
    const modal = useModal();
    const { t } = useTranslation();
    const modalRef = useRef(null);
    const [dragPosition, setDragPosition] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    // 드래그 시작 위치 저장
    const touchStart = useRef(0);
    const startPosition = useRef(0);

    const handleTouchStart = (e) => {
        touchStart.current = e.touches[0].clientY;
        startPosition.current = dragPosition;
        setIsDragging(true);
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        
        const deltaY = e.touches[0].clientY - touchStart.current;
        // 위로 드래그는 제한
        const newPosition = Math.max(0, deltaY);
        setDragPosition(newPosition);
    };

    const closeWithAnimation = () => {
        setIsClosing(true);
        setTimeout(() => {
            modal.remove();
        }, 300); // 애니메이션 시간과 동일하게 설정
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
        // 일정 거리 이상 드래그되면 모달 닫기
        if (dragPosition > 150) {
            closeWithAnimation();
        } else {
            // 그렇지 않으면 원위치
            setDragPosition(0);
        }
    };

    // 매장 정보 가공
    const storeInfo = {
        name: store.place_name || store.storeName,
        address: store.address_name || store.address,
        latitude: store.y || store.latitudeByX,
        longitude: store.x || store.longitudeByY
    };

    // 네비게이션 URL 생성
    const getNaverNavigationUrl = () => {
        const storeLat = parseFloat(storeInfo.latitude);
        const storeLng = parseFloat(storeInfo.longitude);
        const storeName = encodeURIComponent(storeInfo.name);
        return {
            appUrl: `nmap://route/car?dlat=${storeLat}&dlng=${storeLng}&dname=${storeName}`,
            webUrl: `https://map.naver.com/v5/entry/driving/${storeLat},${storeLng}/${storeName}`
        };
    };

    const getKakaoNavigationUrl = () => {
        const storeLat = parseFloat(storeInfo.latitude);
        const storeLng = parseFloat(storeInfo.longitude);
        const storeName = encodeURIComponent(storeInfo.name);
        return {
            appUrl: `kakao://route?sp=0,0&ep=${storeLat},${storeLng}&by=car`,
            webUrl: `https://map.kakao.com/link/to/${storeName},${storeLat},${storeLng}`
        };
    };

    return (
        <>
            {!isMapView && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 500,
                        opacity: isClosing ? 0 : 1,
                        transition: 'opacity 0.3s ease-out'
                    }}
                    onClick={closeWithAnimation}
                />
            )}
            <div
                ref={modalRef}
                style={{
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 86,
                    backgroundColor: 'white',
                    borderRadius: '24px 24px 0 0',
                    zIndex: 50,
                    transform: `translateY(${isClosing ? '100%' : dragPosition + 'px'})`,
                    transition: isDragging ? 'none' : 'transform 0.3s ease-out',
                    boxShadow: isMapView ? '0 -4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                    maxHeight: isMapView ? 'calc(50vh - 80px)' : 'calc(70vh - 60px)',
                    overflowY: 'auto',
                    animation: isClosing ? 'none' : 'slideUp 0.3s ease-out'
                }}
            >
                {/* 드래그 핸들 */}
                <div 
                    style={{
                        width: '100%',
                        height: '20px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'grab',
                        touchAction: 'none',
                        userSelect: 'none',
                        position: 'relative'
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div
                        style={{
                            width: '40px',
                            height: '4px',
                            backgroundColor: '#E5E5E5',
                            borderRadius: '2px',
                            margin: '8px 0'
                        }}
                    />
                    <div
                        onClick={closeWithAnimation}
                        style={{
                            position: 'absolute',
                            right: '16px',
                            top: '30px',
                            transform: 'translateY(-50%)',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: '#999',
                            fontSize: '20px',
                            fontWeight: '900',
                            borderRadius: '50%',
                            backgroundColor: '#f5f5f5',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        ×
                    </div>
                </div>
                
                {store ? (
                    <VerticalFlex>
                        <FlexChild padding={"16px 20px"}>
                            <VerticalFlex gap={12}>
                                {/* 매장명과 평점 */}
                                <FlexChild>
                                    <HorizontalFlex alignItems="center" gap={8}>
                                        <FlexChild flex={1}>
                                            <P weight={700} size={20} color={"#333"}>{storeInfo.name}</P>
                                        </FlexChild>
                                        <FlexChild width={"auto"}>
                                            {/* <div style={{
                                                backgroundColor: "#f5f5f5",
                                                borderRadius: "8px",
                                                padding: "6px 10px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px"
                                            }}>
                                                <img src={store.logoUrl || puffuIcon} alt="로고" style={{ width: "24px", height: "24px", borderRadius: "4px" }} />
                                                <P color={"#666"} size={16} weight={600}>8</P>
                                            </div> */}
                                        </FlexChild>
                                    </HorizontalFlex>
                                </FlexChild>

                                {/* 주소와 거리 */}
                                <FlexChild>
                                    <VerticalFlex gap={2}>
                                        <P color={"#666"} size={14}>{storeInfo.address}</P>
                                        <P color={"#999"} size={13}>357m · 대전 서구</P>
                                    </VerticalFlex>
                                </FlexChild>
                            </VerticalFlex>
                        </FlexChild>

                        {/* 네비게이션 버튼 */}
                        <FlexChild padding={"0 20px 16px 20px"}>
                            <HorizontalFlex gap={8}>
                                <FlexChild flex={1}>
                                    <a
                                        href={getNaverNavigationUrl().appUrl}
                                        onClick={(e) => {
                                            const timeout = setTimeout(() => {
                                                window.location.href = getNaverNavigationUrl().webUrl;
                                            }, 1000);
                                            window.onblur = () => {
                                                clearTimeout(timeout);
                                            };
                                        }}
                                        style={{ 
                                            display: "block",
                                            width: "100%",
                                            fontWeight: "600",
                                            textDecoration: "none",
                                            color: "#03C75A",
                                            backgroundColor: "white",
                                            padding: "12px 0",
                                            borderRadius: "8px",
                                            textAlign: "center",
                                            border: "1.5px solid #03C75A",
                                            fontSize: "14px"
                                        }}
                                    >
                                        {t("네이버 지도")}
                                    </a>
                                </FlexChild>
                                <FlexChild flex={1}>
                                    <a
                                        href={getKakaoNavigationUrl().appUrl}
                                        onClick={(e) => {
                                            const timeout = setTimeout(() => {
                                                window.location.href = getKakaoNavigationUrl().webUrl;
                                            }, 1000);
                                            window.onblur = () => {
                                                clearTimeout(timeout);
                                            };
                                        }}
                                        style={{ 
                                            display: "block",
                                            width: "100%",
                                            fontWeight: "600",
                                            textDecoration: "none",
                                            color: "#000000",
                                            backgroundColor: "white",
                                            padding: "12px 0",
                                            borderRadius: "8px",
                                            textAlign: "center",
                                            border: "1.5px solid #FFE812",
                                            fontSize: "14px"
                                        }}
                                    >
                                        {t("카카오 맵")}
                                    </a>
                                </FlexChild>
                            </HorizontalFlex>
                        </FlexChild>
                    </VerticalFlex>
                ) : (
                    <P>매장 정보를 불러오는 중...</P>
                )}

                <style jsx>{`
                    @keyframes slideUp {
                        from {
                            transform: translateY(100%);
                        }
                        to {
                            transform: translateY(0);
                        }
                    }
                `}</style>

                <style jsx global>{`
                    .nice-modal-portal {
                        z-index: 50 !important;
                    }
                `}</style>
            </div>
        </>
    );
});

export default StoreDetailModal;
